import type { Key } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useLocalPageData, useLocalPagePaginatedTrendingPetitionsData } from 'src/app/pages/local/pageContext';
import { useQueryParamValue, useUpdateQueryString } from 'src/app/shared/hooks/location';

type Result = ModelHookResult<
	{
		displayed: boolean;
		resultsAreFiltered: boolean;
		tags: Array<{ id: string; name: string; selected: boolean }>;
	},
	{ updateFilter: (id: string, filteredSectionId: Key) => void; clearFilters: (filteredSectionId: Key) => void }
>;

export function useTagsFilter(): Result {
	const {
		data: { popularTags },
	} = useLocalPageData();
	const {
		actions: { refreshData },
	} = useLocalPagePaginatedTrendingPetitionsData();

	const filterQueryParam = useQueryParamValue('list_tags_filter');
	const filterTagIds = filterQueryParam ? filterQueryParam.split(',') : [];
	const updateQueryParams = useUpdateQueryString();
	const trackEvent = useTracking();

	return {
		data: {
			displayed: popularTags.length > 0,
			resultsAreFiltered: filterTagIds.length > 0,
			tags: popularTags.map((popularTag) => ({
				...popularTag,
				selected: filterTagIds.includes(popularTag.id),
			})),
		},

		actions: {
			updateFilter: (id: string, filteredSectionId: Key) => {
				void trackEvent('local_page_petition_filters_click', { tag_id: id, sort_id: filteredSectionId.toString() });
				updateQueryParams(
					{
						list_tags_filter: filterTagIds.includes(id)
							? filterTagIds.filter((tagId) => tagId !== id).join(',')
							: [...filterTagIds, id].join(','),
					},
					{ replace: true },
				);
				refreshData();
			},
			clearFilters: (filteredSectionId: Key) => {
				if (!filterTagIds.length) return;

				void trackEvent('local_page_petition_clear_filters_click', { sort_id: filteredSectionId.toString() });
				updateQueryParams(
					{
						list_tags_filter: '',
					},
					{ replace: true },
				);
				refreshData();
			},
		},
	};
}
